/**
 * /learn/prizes/history — "My Prize History" (Figma 9169:57695 / 57796).
 *
 * Two card sections on the pastel hero gradient:
 *   - "Recently Claimed Prizes"  — pending claims (on their way), orange chip
 *   - "Already Received Prizes"  — fulfilled claims, dark "Arrived {date}" chip
 * Each section has its own keyword search; cards reuse the dashboard's
 * product-card look with an "Open" button to the prize's detail page.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { GiftIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { EASE_OUT, slideUp } from '@/lib/motion';
import { useImageUrl } from '../../services/uploads';
import { useMarkClaimReceived, usePrizeClaims } from '../../hooks/learner/usePrizes';
import { BackPill } from '../../components/programs/preview/PreviewWelcomeHero';
import type { LearnerPrizeClaim } from '../../types/learnerPrizes';

function arrivedLabel(iso: string | null): string {
  if (!iso) return 'Arrived';
  const d = new Date(iso);
  return `Arrived ${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(
    d.getFullYear()
  ).slice(2)}`;
}

function SectionSearch({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <label className="relative flex items-center gap-2 w-full sm:max-w-[260px] h-11 px-3 rounded-lg border border-text-primary/60 dark:border-border-primary/60 bg-surface-elevated focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 transition-[border-color,box-shadow] duration-fast ease-out-strong">
      <MagnifyingGlassIcon className="w-5 h-5 text-text-primary/60" aria-hidden="true" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="key words.."
        aria-label={label}
        className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm text-text-primary placeholder:text-text-primary/60"
      />
    </label>
  );
}

function ClaimCard({ claim, index }: { claim: LearnerPrizeClaim; index: number }) {
  const reduceMotion = useReducedMotion();
  const [coverUrl] = useImageUrl(claim.prize?.media_urls?.[0] ?? null);
  const received = useMarkClaimReceived();
  const fulfilled = claim.status === 'fulfilled';

  return (
    <motion.article
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: EASE_OUT, delay: reduceMotion ? 0 : index * 0.04 }}
      className="rounded-2xl border border-border-primary/20 bg-background-primary dark:bg-grey-800 p-3 flex flex-col gap-3"
    >
      <div className="relative aspect-square overflow-hidden rounded-xl bg-text-tertiary/5">
        {coverUrl ? (
          <img src={coverUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, rgb(var(--color-primary) / 0.18), rgb(var(--color-secondary) / 0.18))',
            }}
          />
        )}
      </div>

      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-text-primary line-clamp-1">
          {claim.prize?.title ?? 'Prize'}
        </h3>
        <div className="flex items-center gap-1">
          <span className="text-sm font-bold text-text-primary tabular-nums">
            {claim.points_spent.toLocaleString()}
          </span>
          <StarIcon className="w-4 h-4 text-warning-500" aria-hidden="true" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mt-auto">
        {fulfilled ? (
          <span className="inline-flex items-center h-7 px-2.5 rounded-md bg-text-primary text-surface-elevated text-[11px] font-bold">
            {arrivedLabel(claim.fulfilled_at)}
          </span>
        ) : (
          <span className="inline-flex items-center h-7 px-2.5 rounded-md bg-secondary-500 text-[11px] font-bold">
            On its way
          </span>
        )}
        <Link
          to={`/learn/prizes/${claim.prize_id}/claim`}
          className="inline-flex items-center justify-center h-8 px-4 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-xs font-bold focus-ring active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
        >
          Open
        </Link>
      </div>

      {!fulfilled && (
        // We aren't a shipping company — the learner tells us it arrived.
        <button
          type="button"
          onClick={() => received.mutate(claim.id)}
          disabled={received.isPending}
          className="w-full h-8 rounded-full border border-success-500/50 text-success-600 dark:text-success-400 text-xs font-bold hover:bg-success-500/10 focus-ring active:scale-[0.98] transition-[background-color,transform] duration-fast ease-out-strong disabled:opacity-60"
        >
          {received.isPending ? 'Saving…' : 'Already received'}
        </button>
      )}
    </motion.article>
  );
}

function ClaimSection({
  title,
  claims,
  emptyText,
}: {
  title: string;
  claims: LearnerPrizeClaim[];
  emptyText: string;
}) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return claims;
    return claims.filter((c) => (c.prize?.title ?? '').toLowerCase().includes(q));
  }, [claims, search]);

  return (
    <section className="rounded-3xl border border-border-primary/30 bg-surface-elevated shadow-md p-5 sm:p-8 space-y-5">
      <h2 className="text-xl sm:text-2xl font-bold text-primary-500 tracking-tight">{title}</h2>
      <SectionSearch value={search} onChange={setSearch} label={`Search ${title}`} />
      {filtered.length === 0 ? (
        <div className="py-10 text-center space-y-2">
          <GiftIcon className="w-9 h-9 mx-auto text-text-tertiary" />
          <p className="text-sm text-text-secondary">
            {search ? 'Nothing matches that keyword.' : emptyText}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {filtered.map((claim, i) => (
            <ClaimCard key={claim.id} claim={claim} index={i} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function PrizeHistoryPage() {
  const navigate = useNavigate();
  const { claims, isLoading } = usePrizeClaims({ perPage: 100 });

  const pending = claims.filter((c) => c.status === 'pending');
  const fulfilled = claims.filter((c) => c.status === 'fulfilled');

  return (
    <motion.div {...slideUp} className="space-y-6">
      {/* Breadcrumb path — PageHeader style (no standard header on this page) */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5">
        <Link
          to="/learn/dashboard"
          className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
        >
          Home
        </Link>
        <span className="body-small text-text-secondary">•</span>
        <Link
          to="/learn/prizes"
          className="body-small text-text-secondary hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
        >
          Points &amp; Prizes
        </Link>
        <span className="body-small text-text-secondary">•</span>
        <span className="body-small text-primary-600 dark:text-primary-350 font-medium">Prize History</span>
      </nav>

      <BackPill label="Go back" onClick={() => navigate('/learn/prizes')} />

      {isLoading ? (
        <div className="rounded-3xl border border-border-primary/20 p-8 animate-pulse space-y-4">
          <div className="h-6 w-64 rounded bg-text-tertiary/10" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-2xl bg-text-tertiary/10" />
            ))}
          </div>
        </div>
      ) : (
        <>
          <ClaimSection
            title="Recently Claimed Prizes"
            claims={pending}
            emptyText="No prizes on the way right now — claim one from the Prizes page!"
          />
          <ClaimSection
            title="Already Received Prizes"
            claims={fulfilled}
            emptyText="Prizes you have received will appear here."
          />
        </>
      )}
    </motion.div>
  );
}
